import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { FaTimes } from 'react-icons/fa';
import { Button } from './Button';

/**
 * Modal variants using CVA
 */
export const modalVariants = cva(
  'relative rounded-2xl sm:rounded-2xl rounded-xl bg-[var(--surface-base)] shadow-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-scale',
  {
    variants: {
      size: {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-[95vw] max-h-[95vh]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Modal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal should close
 * @param {'sm'|'md'|'lg'|'xl'|'full'} [props.size='md']
 * @param {string} [props.className]
 * @param {boolean} [props.closeOnOverlayClick=true]
 * @param {boolean} [props.showCloseButton=true]
 * @param {boolean} [props.elevated=false] - Render above other layered overlays
 *   (e.g. a react-bootstrap Modal at z-index ~1055). Use for a modal that must
 *   sit on top of another already-open modal, such as a confirmation dialog.
 * @param {React.ReactNode} props.children
 */
const Modal = ({
  isOpen,
  onClose,
  size,
  className,
  closeOnOverlayClick = true,
  showCloseButton = true,
  elevated = false,
  children,
  ...props
}) => {
  // Handle escape key
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    document.addEventListener('keydown', handleEscape);
    // Restore whatever overflow was in effect when we opened — not a hardcoded
    // 'unset'. If this modal is layered over another modal that already locked
    // body scroll (e.g. a react-bootstrap modal), that lock is preserved on
    // close instead of being wiped out (which would let the page scroll behind
    // the still-open modal).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      // Default overlay sits at z-50 (above page content). When `elevated`, use
      // an inline z-index above react-bootstrap's modal layer (~1055) so a
      // confirmation raised over an already-open bootstrap modal isn't hidden
      // behind it. Inline style avoids depending on a Tailwind arbitrary-value
      // class being generated. Kept below toasts/select portals (9999).
      className={cn('fixed inset-0 flex items-center justify-center p-4', !elevated && 'z-50')}
      style={elevated ? { zIndex: 1200 } : undefined}
      {...props}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div className={cn(modalVariants({ size }), className)}>
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors rounded-lg hover:bg-[var(--bg-tertiary)]"
            aria-label="Close modal"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

/**
 * ModalHeader Component
 */
const ModalHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col space-y-1.5 p-6 pb-4 border-b border-[var(--border-secondary)]',
      className
    )}
    {...props}
  />
));
ModalHeader.displayName = 'ModalHeader';

/**
 * ModalTitle Component
 */
const ModalTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'font-display text-xl font-semibold leading-none tracking-tight text-[var(--text-primary)]',
      className
    )}
    {...props}
  />
));
ModalTitle.displayName = 'ModalTitle';

/**
 * ModalDescription Component
 */
const ModalDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-[var(--text-secondary)]', className)}
    {...props}
  />
));
ModalDescription.displayName = 'ModalDescription';

/**
 * ModalBody Component
 */
const ModalBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('p-6', className)}
    {...props}
  />
));
ModalBody.displayName = 'ModalBody';

/**
 * ModalFooter Component
 */
const ModalFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center justify-end gap-2 p-6 pt-4 border-t border-[var(--border-secondary)]',
      className
    )}
    {...props}
  />
));
ModalFooter.displayName = 'ModalFooter';

/**
 * ConfirmModal - Pre-built confirmation modal
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  description = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
  // A confirmation is the most urgent interaction — always render it on top,
  // including over an already-open react-bootstrap modal (e.g. "Manage Views").
  elevated = true,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" elevated={elevated}>
      <ModalBody className="pt-6">
        <div className="text-center sm:text-left">
          <ModalTitle>{title}</ModalTitle>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {description}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={loading}>
          {cancelText}
        </Button>
        <Button variant={variant} onClick={onConfirm} loading={loading}>
          {confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
  ConfirmModal
};
export default Modal;
